import { Heatmap } from '@ant-design/plots'
import { Empty } from 'antd'

const SocialHeatMap = ({
  data,
  theme = 'classicDark',
  chartHeight = 300,
  chartWidth = 670,
  axisVisibility = 'both',
}) => {
  const displayData = data?.dayHourHeatMaps ?? []

  if (displayData.length === 0) {
    return (
      <div
        style={{
          height: chartHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme === 'classicDark' ? '#141414' : '#f5f5f5',
        }}
      >
        <Empty description="No Data Available" />
      </div>
    )
  }

  const colorPalette = [
    'rgba(179, 218, 251, 0.3)',
    'rgba(137, 194, 245, 0.4)',
    'rgba(103, 173, 238, 0.5)',
    'rgba(74, 152, 230, 0.6)',
    'rgba(59, 136, 217, 0.7)',
    'rgba(51, 114, 184, 0.8)',
    'rgba(43, 91, 146, 0.9)',
    'rgba(34, 69, 111, 1)',
    'rgba(23, 51, 84, 1)',
    'rgba(12, 29, 47, 1)',
  ]

  const aggregateByDay = (data) => {
    const dayMap = {}
    data.forEach(({ day, value }) => {
      dayMap[day] = (dayMap[day] || 0) + value
    })
    return Object.entries(dayMap).map(([day, value]) => ({ day, value }))
  }

  const aggregateByHour = (data) => {
    const hourMap = {}
    data.forEach(({ hour, value }) => {
      hourMap[hour] = (hourMap[hour] || 0) + value
    })
    return Object.entries(hourMap).map(([hour, value]) => ({
      hour: parseInt(hour, 10),
      value,
    }))
  }

  const dayOrder = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

  const processedData = displayData.map((item) => ({
    ...item,
    hour: parseInt(item.hour.split(':')[0], 10),
  }))

  processedData.sort((a, b) => {
    const dayComparison = dayOrder.indexOf(a.day) - dayOrder.indexOf(b.day)
    return dayComparison !== 0 ? dayComparison : a.hour - b.hour
  })

  const dayData = aggregateByDay(processedData)
  const hourData = aggregateByHour(processedData)

  const getValues = (data) =>
    data.map((d) => (typeof d.value === 'number' ? d.value : 0))

  const dayHourValues = getValues(processedData)
  const dayValues = getValues(dayData)
  const hourValues = getValues(hourData)

  const dayHourMinMax = {
    min: Math.min(...dayHourValues),
    max: Math.max(...dayHourValues),
  }

  const dayMinMax = {
    min: Math.min(...dayValues),
    max: Math.max(...dayValues),
  }

  const hourMinMax = {
    min: Math.min(...hourValues),
    max: Math.max(...hourValues),
  }

  const dayAndHourconfig = {
    theme,
    height: chartHeight,
    width: chartWidth,
    autoFit: false,
    data: processedData,
    xField: 'hour',
    yField: 'day',
    colorField: 'value',
    mark: 'cell',
    axis: {
      x: {
        grid: null,
        line: null,
        tick: null,
        label: {
          style: { fill: '#aaa' },
          formatter: (val) => {
            if (['0', '6', '12', '8'].includes(val)) {
              const hour = parseInt(val, 10)
              if (hour === 0) return '12AM'
              if (hour === 12) return '12PM'
              return hour < 12 ? `${hour}AM` : `${hour - 12}PM`
            }
            return ''
          },
        },
      },
      y: {
        grid: null,
        line: null,
        tick: null,
        label: { style: { fill: '#aaa' } },
      },
    },
    tooltip: {
      items: [
        {
          field: 'value',
          name: 'Count',
        },
        {
          field: 'hour',
          name: 'Hour',
          formatter: (val) => {
            if (val === 0) return '12AM'
            if (val === 12) return '12PM'
            return val < 12 ? `${val}AM` : `${val - 12}PM`
          },
        },
        {
          field: 'day',
          name: 'Day',
        },
      ],
    },
    color: colorPalette,
    legend: false,
    style: {
      lineWidth: 2,
      stroke: theme === 'classicDark' ? '#141414' : '#f5f5f5',
      opacity: 0.8,
      shadowColor: 'rgba(0, 0, 0, 0.2)',
      shadowBlur: 5,
    },

    scale: {
      x: {
        domain: [
          0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
          20, 21, 22, 23,
        ],
      },
      y: {
        domain: dayOrder,
      },
      color: {
        type: 'quantize',
        domain: [dayHourMinMax.min, dayHourMinMax.max],
        range: colorPalette,
      },
    },
  }

  const configDay = {
    theme,
    height: chartHeight,
    autoFit: false,
    data: dayData,
    yField: 'day',
    colorField: 'value',
    mark: 'cell',
    tooltip: {
      items: [
        {
          field: 'value',
          name: 'Count',
        },
        {
          field: 'day',
          name: 'Day',
        },
      ],
    },
    axis: {
      y: {
        grid: null,
        line: null,
        tick: null,
        label: { style: { fill: '#aaa' } },
      },
    },
    color: colorPalette,
    legend: false,
    style: {
      stroke: '#000',
    },
    scale: {
      y: {
        domain: dayOrder,
      },
      color: {
        type: 'quantize',
        domain: [dayMinMax.min, dayMinMax.max],
        range: colorPalette,
      },
    },
  }

  const configHour = {
    theme,
    height: chartHeight,
    autoFit: false,
    data: hourData,
    xField: 'hour',
    colorField: 'value',
    mark: 'cell',
    tooltip: {
      items: [
        {
          field: 'value',
          name: 'Count',
        },
        {
          field: 'hour',
          name: 'Hour',
          formatter: (val) => {
            if (val === 0) return '12AM'
            if (val === 12) return '12PM'
            return val < 12 ? `${val}AM` : `${val - 12}PM`
          },
        },
      ],
    },
    axis: {
      x: {
        grid: null,
        line: null,
        tick: null,
        label: {
          style: { fill: '#aaa' },
          formatter: (val) => {
            if (['0', '6', '12', '8'].includes(val)) {
              const hour = parseInt(val, 10)
              if (hour === 0) return '12AM'
              if (hour === 12) return '12PM'
              return hour < 12 ? `${hour}AM` : `${hour - 12}PM`
            }
            return ''
          },
        },
      },
    },
    color: colorPalette,
    legend: false,
    style: {
      stroke: '#000',
    },
    scale: {
      x: {
        domain: [
          0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19,
          20, 21, 22, 23,
        ],
      },
      color: {
        type: 'quantize',
        domain: [hourMinMax.min, hourMinMax.max],
        range: colorPalette,
      },
    },
  }

  let finalConfig = {}
  if (axisVisibility == 'both') {
    finalConfig = dayAndHourconfig
  } else if (axisVisibility == 'x') {
    finalConfig = configHour
  } else {
    finalConfig = configDay
  }

  return <Heatmap {...finalConfig} />
}

export default SocialHeatMap
